/*!
 * Mobix Motion Engine v1.0
 * One shared motion layer for the whole theme.
 * - Declarative API: data-mx-reveal, data-mx-split, data-mx-tilt, data-mx-magnetic,
 *   data-mx-parallax, data-mx-progress, data-mx-count
 * - Auto-binds Mobix sections by selector, so existing sections get motion without markup changes
 * - Only transform/opacity (and the individual `translate` property); will-change is set only while moving
 * - RTL aware, prefers-reduced-motion aware, theme-editor aware
 * - Lenis (desktop only) and GSAP + ScrollTrigger are loaded lazily and are optional:
 *   if they fail to load, nothing breaks.
 */
(function () {
  'use strict';

  if (window.MX && window.MX.version) return;

  var d = document;
  var html = d.documentElement;
  var w = window;

  var mq = function (q) { return w.matchMedia ? w.matchMedia(q) : { matches: false, addEventListener: function () {} }; };
  var RM = mq('(prefers-reduced-motion: reduce)');
  var FINE = mq('(hover: hover) and (pointer: fine)');
  var DESK = mq('(min-width: 990px)');
  var EDITOR = !!(w.Shopify && w.Shopify.designMode);

  var LIB = {
    gsap: 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js',
    st: 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js',
    lenis: 'https://cdn.jsdelivr.net/npm/lenis@1.1.13/dist/lenis.min.js'
  };

  var MX = {
    version: '1.0.0',
    reduced: RM.matches,
    lenis: null,
    _scrollItems: [],
    _visible: new Set(),
    _ticking: false,
    _scripts: {}
  };
  w.MX = MX;

  /* ---------------------------------------------------------------- utils */

  function qa(sel, ctx) { return Array.prototype.slice.call((ctx || d).querySelectorAll(sel)); }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function isRTL(el) {
    try { return getComputedStyle(el).direction === 'rtl'; } catch (e) { return false; }
  }
  function inView(el) {
    var r = el.getBoundingClientRect();
    return r.top < (w.innerHeight || html.clientHeight) && r.bottom > 0;
  }
  function loadScript(src) {
    if (MX._scripts[src]) return MX._scripts[src];
    MX._scripts[src] = new Promise(function (res, rej) {
      var s = d.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = function () { res(); };
      s.onerror = function () { rej(new Error('MX: failed to load ' + src)); };
      d.head.appendChild(s);
    });
    return MX._scripts[src];
  }
  function idle(fn) {
    if ('requestIdleCallback' in w) w.requestIdleCallback(fn, { timeout: 2500 });
    else setTimeout(fn, 600);
  }

  /* ------------------------------------------------------------ reveals */

  var revealIO = null;
  function getRevealIO() {
    if (revealIO || !('IntersectionObserver' in w)) return revealIO;
    revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        el.classList.add('mx-in');
        revealIO.unobserve(el);
        if (el.hasAttribute('data-mx-count')) runCount(el);
        var done = function () { el.classList.add('mx-done'); el.removeEventListener('transitionend', done); };
        el.addEventListener('transitionend', done);
        setTimeout(done, 1600);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    return revealIO;
  }

  function reveal(el, type, delayIndex) {
    if (el.hasAttribute('data-mx-bound-r')) return;
    el.setAttribute('data-mx-bound-r', '');
    if (MX.reduced || EDITOR || !('IntersectionObserver' in w)) {
      if (el.hasAttribute('data-mx-count')) runCount(el);
      return;
    }
    /* never hide something the visitor can already see */
    if (inView(el)) {
      if (el.hasAttribute('data-mx-count')) runCount(el);
      return;
    }
    el.setAttribute('data-mx-reveal', type || el.getAttribute('data-mx-reveal') || 'up');
    if (isRTL(el)) el.setAttribute('data-mx-rtl', '');
    if (delayIndex) el.style.setProperty('--mx-d', Math.min(delayIndex, 8) * 70 + 'ms');
    getRevealIO().observe(el);
  }

  /* split text into words (never letters: Hebrew shaping needs whole words) */
  function split(el) {
    if (el.hasAttribute('data-mx-bound-s')) return;
    el.setAttribute('data-mx-bound-s', '');
    if (MX.reduced || EDITOR || !('IntersectionObserver' in w) || inView(el)) return;
    var label = el.textContent.replace(/\s+/g, ' ').trim();
    if (!label) return;
    var idx = 0;
    var walk = function (node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (n) {
        if (n.nodeType === 3) {
          var parts = n.nodeValue.split(/(\s+)/);
          var frag = d.createDocumentFragment();
          parts.forEach(function (p) {
            if (!p) return;
            if (/^\s+$/.test(p)) { frag.appendChild(d.createTextNode(p)); return; }
            var o = d.createElement('span');
            o.className = 'mx-w';
            var i = d.createElement('span');
            i.className = 'mx-wi';
            i.style.setProperty('--mx-i', idx++);
            i.textContent = p;
            o.appendChild(i);
            frag.appendChild(o);
          });
          n.parentNode.replaceChild(frag, n);
        } else if (n.nodeType === 1 && !/^(BR|SVG|IMG)$/i.test(n.nodeName)) {
          walk(n);
        }
      });
    };
    walk(el);
    el.setAttribute('aria-label', label);
    qa('.mx-w', el).forEach(function (s) { s.setAttribute('aria-hidden', 'true'); });
    el.setAttribute('data-mx-split', '');
    getRevealIO().observe(el);
  }

  /* number counters: animate the first number found in the first text node */
  function runCount(el) {
    if (el.__mxCounted) return;
    el.__mxCounted = true;
    var tn = null;
    for (var i = 0; i < el.childNodes.length; i++) {
      if (el.childNodes[i].nodeType === 3 && /\d/.test(el.childNodes[i].nodeValue)) { tn = el.childNodes[i]; break; }
    }
    if (!tn) return;
    var src = tn.nodeValue;
    var m = src.match(/(\d[\d,]*\.?\d*)/);
    if (!m) return;
    var raw = m[1];
    var to = parseFloat(raw.replace(/,/g, ''));
    if (!isFinite(to) || to === 0) return;
    var dec = (raw.split('.')[1] || '').length;
    var comma = raw.indexOf(',') > -1;
    if (MX.reduced) return;
    var t0 = null;
    var fmt = function (v) {
      var s = v.toFixed(dec);
      if (comma) s = s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return s;
    };
    var step = function (ts) {
      if (!t0) t0 = ts;
      var p = Math.min(1, (ts - t0) / 1400);
      var e = 1 - Math.pow(1 - p, 3);
      tn.nodeValue = src.replace(raw, fmt(to * e));
      if (p < 1) requestAnimationFrame(step);
      else tn.nodeValue = src;
    };
    tn.nodeValue = src.replace(raw, fmt(0));
    requestAnimationFrame(step);
  }

  /* --------------------------------------------------------------- tilt */

  function tilt(el, max) {
    if (el.hasAttribute('data-mx-bound-t')) return;
    el.setAttribute('data-mx-bound-t', '');
    if (MX.reduced || EDITOR) return;
    max = +max || 7;
    el.classList.add('mx-tilt');
    if (!el.querySelector(':scope > .mx-glare')) {
      var g = d.createElement('span');
      g.className = 'mx-glare';
      g.setAttribute('aria-hidden', 'true');
      el.appendChild(g);
    }
    if (FINE.matches) {
      var st = { x: 0, y: 0, tx: 0, ty: 0, raf: 0, on: false };
      var frame = function () {
        st.x = lerp(st.x, st.tx, 0.14);
        st.y = lerp(st.y, st.ty, 0.14);
        el.style.transform = 'perspective(900px) rotateX(' + (st.y * -max).toFixed(2) + 'deg) rotateY(' + (st.x * max).toFixed(2) + 'deg) translate3d(0,' + (st.on ? -4 : 0) + 'px,0)';
        el.style.setProperty('--mx-gx', (50 + st.x * 50).toFixed(1) + '%');
        el.style.setProperty('--mx-gy', (50 + st.y * 50).toFixed(1) + '%');
        if (Math.abs(st.x - st.tx) > 0.002 || Math.abs(st.y - st.ty) > 0.002 || st.on) {
          st.raf = requestAnimationFrame(frame);
        } else {
          st.raf = 0;
          if (!st.on) { el.style.transform = ''; el.style.willChange = ''; }
        }
      };
      var kick = function () { if (!st.raf) st.raf = requestAnimationFrame(frame); };
      el.addEventListener('pointerenter', function () {
        if (el.hasAttribute('data-mx-reveal') && !el.classList.contains('mx-done')) return;
        st.on = true; el.classList.add('mx-tilting'); el.style.willChange = 'transform'; kick();
      });
      el.addEventListener('pointermove', function (e) {
        if (!st.on) return;
        var r = el.getBoundingClientRect();
        st.tx = clamp((e.clientX - r.left) / r.width * 2 - 1, -1, 1);
        st.ty = clamp((e.clientY - r.top) / r.height * 2 - 1, -1, 1);
        kick();
      });
      el.addEventListener('pointerleave', function () {
        st.on = false; st.tx = 0; st.ty = 0; el.classList.remove('mx-tilting'); kick();
      });
    } else {
      /* touch: tilt is driven by scroll position instead of the device gyroscope
         (iOS requires a permission prompt for motion sensors) */
      registerScroll(el, function (p) {
        if (el.hasAttribute('data-mx-reveal') && !el.classList.contains('mx-done')) return;
        var v = (p - 0.5) * 2;
        el.style.transform = 'perspective(900px) rotateX(' + (v * max * 0.45).toFixed(2) + 'deg)';
        el.style.setProperty('--mx-gy', (50 + v * 40).toFixed(1) + '%');
      });
    }
  }

  /* ----------------------------------------------------------- magnetic */

  function magnetic(el, strength) {
    if (el.hasAttribute('data-mx-bound-m')) return;
    el.setAttribute('data-mx-bound-m', '');
    if (MX.reduced || EDITOR) return;
    el.classList.add('mx-mag');
    if (!FINE.matches) return;
    strength = +strength || 0.28;
    var st = { x: 0, y: 0, tx: 0, ty: 0, raf: 0 };
    var frame = function () {
      st.x = lerp(st.x, st.tx, 0.2);
      st.y = lerp(st.y, st.ty, 0.2);
      el.style.translate = st.x.toFixed(2) + 'px ' + st.y.toFixed(2) + 'px';
      if (Math.abs(st.x - st.tx) > 0.05 || Math.abs(st.y - st.ty) > 0.05) st.raf = requestAnimationFrame(frame);
      else { st.raf = 0; if (st.tx === 0 && st.ty === 0) { el.style.translate = ''; el.style.willChange = ''; } }
    };
    var kick = function () { if (!st.raf) st.raf = requestAnimationFrame(frame); };
    el.addEventListener('pointerenter', function () { el.style.willChange = 'translate'; });
    el.addEventListener('pointermove', function (e) {
      var r = el.getBoundingClientRect();
      st.tx = (e.clientX - (r.left + r.width / 2)) * strength;
      st.ty = (e.clientY - (r.top + r.height / 2)) * strength;
      el.style.setProperty('--mx-mx', ((e.clientX - r.left) / r.width * 100).toFixed(1) + '%');
      el.style.setProperty('--mx-my', ((e.clientY - r.top) / r.height * 100).toFixed(1) + '%');
      kick();
    });
    el.addEventListener('pointerleave', function () { st.tx = 0; st.ty = 0; kick(); });
  }

  /* ------------------------------------------------- scroll-linked items */

  var scrollIO = null;
  function getScrollIO() {
    if (scrollIO || !('IntersectionObserver' in w)) return scrollIO;
    scrollIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) MX._visible.add(e.target);
        else MX._visible.delete(e.target);
      });
      requestTick();
    }, { rootMargin: '15% 0px 15% 0px' });
    return scrollIO;
  }

  function registerScroll(el, fn, mode) {
    if (MX.reduced || EDITOR || !getScrollIO()) return;
    var list = el.__mxScroll || (el.__mxScroll = []);
    list.push({ fn: fn, mode: mode || 'through' });
    if (list.length === 1) {
      MX._scrollItems.push(el);
      getScrollIO().observe(el);
    }
  }

  /* progress: 'through' = 0 when the element enters at the bottom, 1 when it leaves at the top.
               'exit'    = 0 while its top is at the viewport top, 1 once it has fully scrolled away. */
  function progressOf(el, mode, vh) {
    var r = el.getBoundingClientRect();
    if (mode === 'exit') return clamp(-r.top / Math.max(1, r.height), 0, 1);
    return clamp((vh - r.top) / (vh + r.height), 0, 1);
  }

  function tick() {
    MX._ticking = false;
    var vh = w.innerHeight || html.clientHeight;
    MX._visible.forEach(function (el) {
      var list = el.__mxScroll;
      if (!list) return;
      for (var i = 0; i < list.length; i++) list[i].fn(progressOf(el, list[i].mode, vh), el);
    });
  }
  function requestTick() {
    if (!MX._ticking) { MX._ticking = true; requestAnimationFrame(tick); }
  }

  function parallax(el, speed) {
    if (el.hasAttribute('data-mx-bound-p')) return;
    el.setAttribute('data-mx-bound-p', '');
    speed = +speed || 0.08;
    el.classList.add('mx-par');
    registerScroll(el, function (p) {
      var vh = w.innerHeight || 800;
      el.style.translate = '0 ' + ((p - 0.5) * vh * speed).toFixed(1) + 'px';
    });
  }

  function progress(el, mode) {
    if (el.hasAttribute('data-mx-bound-g')) return;
    el.setAttribute('data-mx-bound-g', '');
    registerScroll(el, function (p) { el.style.setProperty('--mx-p', p.toFixed(4)); }, mode);
  }

  /* -------------------------------------------------------- declarative */

  function bindDeclarative(root) {
    qa('[data-mx-reveal]:not([data-mx-bound-r])', root).forEach(function (el) { reveal(el, el.getAttribute('data-mx-reveal')); });
    qa('[data-mx-split]:not([data-mx-bound-s])', root).forEach(split);
    qa('[data-mx-tilt]:not([data-mx-bound-t])', root).forEach(function (el) { tilt(el, el.getAttribute('data-mx-tilt')); });
    qa('[data-mx-magnetic]:not([data-mx-bound-m])', root).forEach(function (el) { magnetic(el, el.getAttribute('data-mx-magnetic')); });
    qa('[data-mx-parallax]:not([data-mx-bound-p])', root).forEach(function (el) { parallax(el, el.getAttribute('data-mx-parallax')); });
    qa('[data-mx-progress]:not([data-mx-bound-g])', root).forEach(function (el) { progress(el, el.getAttribute('data-mx-progress')); });
    qa('[data-mx-count]:not([data-mx-bound-r])', root).forEach(function (el) { reveal(el, 'fade'); });
  }

  /* ------------------------------------------------- Mobix section map */
  /* [selector, behaviours]. `stagger` = closest container used to index siblings for delays. */
  var MAP = [
    /* home: hero video */
    ['.mobix-hero', { progress: 'exit' }],
    /* home: worlds */
    ['[id^="mh-"] .intro h2', { split: 1 }],
    ['[id^="mh-"] .intro .lead', { reveal: 'up' }],
    ['[id^="mh-"] .door', { reveal: 'up', stagger: '.doors', magnetic: 0.16 }],
    ['[id^="mh-"] .ch', { progress: 'through' }],
    ['[id^="mh-"] .ch-head p:not(.word)', { reveal: 'start' }],
    ['[id^="mh-"] .lead-row .panel', { reveal: 'rise3d', tilt: 5 }],
    ['[id^="mh-"] .lead-row .panel img', { parallax: -0.06 }],
    ['[id^="mh-"] .info > *', { reveal: 'up', stagger: '.info' }],
    ['[id^="mh-"] .ri', { reveal: 'up', stagger: '.rail', tilt: 7 }],
    ['[id^="mh-"] .calc h2', { split: 1 }],
    ['[id^="mh-"] .calc .sub', { reveal: 'up' }],
    ['[id^="mh-"] .chip', { reveal: 'up', stagger: '.chips' }],
    ['[id^="mh-"] .stage > *', { reveal: 'rise3d', stagger: '.stage' }],
    ['[id^="mh-"] .promise h2', { split: 1 }],
    ['[id^="mh-"] .pl > div', { reveal: 'up', stagger: '.pl' }],
    ['[id^="mh-"] .rv', { reveal: 'up' }],
    ['[id^="mh-"] .btn', { magnetic: 0.22 }],
    /* home: instagram */
    ['[id^="mi-"] h2', { split: 1 }],
    ['[id^="mi-"] .sub', { reveal: 'up' }],
    ['[id^="mi-"] .follow', { reveal: 'up', magnetic: 0.22 }],
    ['[id^="mi-"] .reel', { reveal: 'rise3d', stagger: '.rail' }],
    /* home: reviews */
    ['[id^="mxh-"] h2', { split: 1 }],
    ['[id^="mxh-"] .h-lead', { reveal: 'up' }],
    ['[id^="mxh-"] .h-kpi > div', { reveal: 'up', stagger: '.h-kpi' }],
    ['[id^="mxh-"] .h-rows', { reveal: 'up' }],
    ['[id^="mxh-"] .h-cta', { magnetic: 0.22 }],
    /* home: whatsapp */
    ['[id^="mw-"] .card', { reveal: 'rise3d' }],
    ['[id^="mw-"] h2', { split: 1 }],
    ['[id^="mw-"] .wa', { magnetic: 0.2 }],
    /* product pages (shared Mobix PDP) */
    ['.mxp .hero .gal', { reveal: 'scale' }],
    ['.mxp .buy > *', { reveal: 'up', stagger: '.buy' }],
    ['.mxp .cta, .mxp .ghost', { magnetic: 0.18 }],
    ['.mxp .band h2, .mxp .sec h2, .mxp .final h2', { split: 1 }],
    ['.mxp .num-cell', { reveal: 'up', stagger: '.nums' }],
    ['.mxp .num', { count: 1 }],
    ['.mxp .flow > li', { reveal: 'up', stagger: '.flow' }],
    ['.mxp .feat', { reveal: 'up', stagger: '.feats', tilt: 4 }],
    ['.mxp .people > div', { reveal: 'start', stagger: '.people' }],
    ['.mxp .who img, .mxp .who .mx-sidevid', { reveal: 'rise3d' }],
    ['.mxp .cmp', { reveal: 'up' }],
    ['.mxp .specs > div', { reveal: 'up', stagger: '.specs' }],
    ['.mxp .promise > div', { reveal: 'up', stagger: '.promise' }],
    ['.mxp details', { reveal: 'up', stagger: '.end' }],
    ['.mxp .lead', { reveal: 'up' }],
    /* product signature sections */
    ['[id^="sg-"] h2', { split: 1 }],
    ['[id^="sg-"] .sub', { reveal: 'up' }],
    ['[id^="sg-"] .grid > *', { reveal: 'rise3d', stagger: '.grid', tilt: 6 }],
    /* header */
    ['.section-header .header__icon, .section-header .header__heading-link', { magnetic: 0.25 }]
  ];

  function staggerIndex(el, containerSel) {
    var c = el.closest(containerSel);
    if (!c) return 0;
    var kids = Array.prototype.slice.call(c.children);
    var i = kids.indexOf(el);
    if (i < 0) {
      var cand = kids.filter(function (k) { return k.contains(el); })[0];
      i = kids.indexOf(cand);
    }
    return Math.max(0, i);
  }

  function bindMap(root) {
    MAP.forEach(function (row) {
      var sel = row[0], b = row[1];
      qa(sel, root).forEach(function (el) {
        if (el.closest('[data-mx-off]')) return;
        if (b.split) split(el);
        if (b.count) el.setAttribute('data-mx-count', '');
        if (b.reveal) reveal(el, b.reveal, b.stagger ? staggerIndex(el, b.stagger) : 0);
        else if (b.count) reveal(el, 'fade');
        if (b.tilt) tilt(el, b.tilt);
        if (b.magnetic) magnetic(el, b.magnetic);
        if (b.parallax) parallax(el, b.parallax);
        if (b.progress) progress(el, b.progress);
      });
    });
  }

  /* -------------------------------------------------------------- header */

  function header() {
    var sec = d.querySelector('.section-header');
    if (!sec) return;
    var set = function () {
      html.classList.toggle('mx-scrolled', (w.pageYOffset || html.scrollTop) > 8);
    };
    set();
    w.addEventListener('scroll', set, { passive: true });

    /* cart bubble pop when the count changes (any source: AJAX cart, drawer, section rendering) */
    var bubble = d.getElementById('cart-icon-bubble');
    if (bubble && 'MutationObserver' in w) {
      var last = bubble.textContent.replace(/\D+/g, '');
      new MutationObserver(function () {
        var now = bubble.textContent.replace(/\D+/g, '');
        if (now !== last) {
          last = now;
          bubble.classList.remove('mx-pop');
          void bubble.offsetWidth;
          bubble.classList.add('mx-pop');
        }
      }).observe(bubble, { childList: true, subtree: true, characterData: true });
    }
  }

  /* --------------------------------------------------------------- Lenis */

  function lenis() {
    if (MX.reduced || EDITOR || !DESK.matches || !FINE.matches) return;
    loadScript(LIB.lenis).then(function () {
      if (!w.Lenis || MX.lenis) return;
      var blockSel = 'cart-drawer, .drawer, .menu-drawer, details-modal, .search-modal, dialog, [data-lenis-prevent], .h-mask, .rail, .track, .chips, textarea, select, iframe';
      var l = new w.Lenis({
        duration: 1.05,
        easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
        smoothWheel: true,
        syncTouch: false,
        prevent: function (node) { return !!(node && node.closest && node.closest(blockSel)); }
      });
      MX.lenis = l;
      html.classList.add('mx-lenis');
      var raf = function (t) { l.raf(t); requestAnimationFrame(raf); };
      requestAnimationFrame(raf);
      l.on('scroll', requestTick);

      /* pause smooth scroll whenever Dawn locks the page (drawers, modals) */
      var sync = function () {
        var b = d.body.classList;
        if (b.contains('overflow-hidden') || b.contains('overflow-hidden-mobile') || b.contains('overflow-hidden-tablet') || b.contains('overflow-hidden-desktop')) l.stop();
        else l.start();
      };
      new MutationObserver(sync).observe(d.body, { attributes: true, attributeFilter: ['class'] });

      /* same-page anchors (e.g. #mx-calc, #buy) glide instead of jumping */
      d.addEventListener('click', function (e) {
        var a = e.target.closest && e.target.closest('a[href^="#"]');
        if (!a) return;
        var id = a.getAttribute('href');
        if (id.length < 2) return;
        var t = d.getElementById(decodeURIComponent(id.slice(1)));
        if (!t) return;
        e.preventDefault();
        var off = -(parseInt(getComputedStyle(t).scrollMarginTop, 10) || 72);
        l.scrollTo(t, { offset: off });
        if (history.replaceState) history.replaceState(null, '', id);
      });
    }).catch(function () {});
  }

  /* ---------------------------------------------- GSAP (opt-in, lazy) */
  /* Product-page sections call MX.gsap().then(function (g) { ... }) for scrubbed timelines. */
  MX.gsap = function () {
    if (MX.reduced) return Promise.reject(new Error('MX: reduced motion'));
    if (MX._gsap) return MX._gsap;
    MX._gsap = loadScript(LIB.gsap).then(function () { return loadScript(LIB.st); }).then(function () {
      w.gsap.registerPlugin(w.ScrollTrigger);
      if (MX.lenis) MX.lenis.on('scroll', w.ScrollTrigger.update);
      return { gsap: w.gsap, ScrollTrigger: w.ScrollTrigger };
    });
    return MX._gsap;
  };

  /* -------------------------------------------------------------- public */

  MX.bind = function (root) {
    root = root || d;
    bindDeclarative(root);
    bindMap(root);
    requestTick();
  };
  MX.reveal = reveal;
  MX.split = split;
  MX.tilt = tilt;
  MX.magnetic = magnetic;
  MX.parallax = parallax;
  MX.progress = progress;
  MX.onScroll = registerScroll;
  MX.isRTL = isRTL;

  /* ---------------------------------------------------------------- boot */

  function boot() {
    if (MX.reduced) html.classList.add('mx-rm');
    html.classList.add('mx-on');
    header();
    MX.bind(d);
    w.addEventListener('scroll', requestTick, { passive: true });
    w.addEventListener('resize', requestTick, { passive: true });
    if (d.readyState === 'complete') idle(lenis);
    else w.addEventListener('load', function () { idle(lenis); });

    /* theme editor: re-bind sections when they are re-rendered */
    d.addEventListener('shopify:section:load', function (e) { MX.bind(e.target); });

    RM.addEventListener && RM.addEventListener('change', function (e) {
      MX.reduced = e.matches;
      html.classList.toggle('mx-rm', e.matches);
      if (e.matches) {
        qa('[data-mx-reveal]').forEach(function (el) { el.classList.add('mx-in', 'mx-done'); });
        qa('[data-mx-split]').forEach(function (el) { el.classList.add('mx-in'); });
        if (MX.lenis) MX.lenis.destroy();
      }
    });
  }

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
